/**
 * Application Route Constants
 *
 * Centralizes route path strings used for navigation so portal switching stays consistent
 * across the main app and the admin portal.
 */

/**
 * Public page routes — accessible without authentication.
 * Used by the PublicNav component and App.tsx routing.
 */
export const PUBLIC_ROUTES = {
  /** Marketing homepage (landing) */
  home: '/',

  /** How it works explanation page */
  howItWorks: '/how-it-works',

  /** FAQ page with grouped question categories */
  faq: '/faq',

  /** Full card gallery with filtering and guest scheduling */
  cards: '/cards',

  /** Public e-card open-animation preview. `{slug}` is the template slug (TPL-NNN). */
  ecardProduct: (slug: string) => `/cards/${encodeURIComponent(slug)}`,

  /** Pricing page with plan comparison */
  pricing: '/pricing',

  /** Public flower catalog page */
  flowers: '/flowers',
  /** Flower product detail (Merchant Center landing). */
  flowerProduct: (publicId: string) => `/flowers/${encodeURIComponent(publicId)}`,
  /** Public mailed-card catalog page */
  mailedCards: '/mailed-cards',
  /** Mailed-card product detail (Merchant Center landing). */
  mailedCardProduct: (slug: string) => `/mailed-cards/${encodeURIComponent(slug)}`,

  /** Public gift catalog page */
  gifts: '/gifts',

  /** Guest occasion-reminder double opt-in confirmation page */
  reminderConfirm: '/reminders/confirm',

  /** Public guides index (lists intent guides; child URLs live under /guides/) */
  guides: '/guides',

  /** GEO intent guide — Auto Send vs reminders (public, indexable) */
  automatedOccasionGiftsGuide: '/guides/automated-occasion-gifts',

  /** AI / MCP hub (public, indexable) — see aiPublicRoutes.ts for use-case paths */
  ai: '/ai',

  /** Guest occasion-reminder unsubscribe confirmation page */
  reminderUnsubscribe: '/reminders/unsubscribe',

  /** Marketing nurture email unsubscribe (tokenized, no auth) */
  marketingUnsubscribe: '/account/marketing-unsubscribe',

  /** Inbox-rescue help for Gmail, Outlook, Yahoo, Apple Mail */
  emailDeliveryHelp: '/help/email-delivery',

  /** Terms of Service (public legal page) */
  terms: '/terms',

  /** Privacy Policy (public legal page) */
  privacy: '/privacy',
} as const;

export const APP_ROUTES = {
  /**
   * Main app default authenticated landing page.
   *
   * Business rule:
   * - "Overview" is the primary landing page label for authenticated users.
   */
  overview: '/overview',

  /**
   * Legacy route kept for backward compatibility (bookmarks, older emails, etc.).
   * Should redirect to `overview`.
   */
  legacyDashboard: '/dashboard',

  /**
   * Authenticated E-Card template gallery (sidebar label: "E-Cards").
   * Centralised so Overview browse links and nav stay aligned.
   */
  eCards: '/templates',

  /** Full list of upcoming events with filters */
  upcomingEvents: '/upcoming-events',

  /**
   * Smile order history (pre-orders + settled charges / receipts) under ACTIVITY (PBI #1198).
   * Personal plan invoices remain on My Account Customer Portal.
   */
  orders: '/orders',

  /** Shared account management page (renders in main app shell) */
  account: '/account',

  /**
   * User settings (account preferences) — alias path for bookmarks and nurture links.
   * Renders the same My Account page as {@link APP_ROUTES.account}.
   */
  settings: '/settings',

  /** Delegated chatbot grants for the signed-in user (PBI #1300) */
  connectedAssistants: '/account/connected-assistants',

  /** Authenticated flower ordering page (feature-gated, renders in AppShell) */
  flowers: '/flower-shop',

  /** Authenticated mailed-card ordering page (feature-gated, renders in AppShell) */
  mailedCards: '/mailed-card-shop',

  /**
   * "Contact Us" inbox — lists the user's own support tickets and lets them
   * file a new one. Renders inside the main AppShell.
   */
  contactUs: '/contact-us',

  /**
   * Single support-ticket thread page. Use the `:id` URL param.
   * Convenience helper: <c>contactUsThread(42) === '/contact-us/42'</c>.
   */
  contactUsThread: '/contact-us/:id',

  /** Admin portal root route (renders in AdminAppShell) */
  adminRoot: '/admin',

  /**
   * Admin "Scheduled Items Overview" — unified upcoming/past timeline of
   * scheduled e-cards and flower-order placements with row-level recovery
   * actions (Send Now / Place Order Now). Renders in AdminAppShell.
   */
  adminOperations: '/admin/operations',

  /**
   * Admin "Support Tickets" inbox — lists every ticket and lets support
   * agents reply / change status. Renders in AdminAppShell.
   */
  adminSupportTickets: '/admin/support-tickets',

  /** Admin single-ticket detail page; uses `:id` URL param. */
  adminSupportTicketDetail: '/admin/support-tickets/:id',

  /** Admin flower orders list and recovery actions. */
  adminFlowerOrders: '/admin/flower-orders',

  /** Admin flower vendor enable/disable and catalog sync (PBI #1081). */
  adminFlowerVendors: '/admin/flower-vendors',

  /** Admin contact relationship catalog (relationship choices + optional AI guidance). */
  adminContactRelationships: '/admin/contact-relationships',

  /** Admin Florist One flower promotions (PBI #1234). */
  adminFlowerPromotions: '/admin/promotions',

  /** Admin mailed card (Cardly) orders list and recovery actions. */
  adminMailedCardOrders: '/admin/mailed-card-orders',

  /** Admin Goody gift catalog. */
  adminGifts: '/admin/gifts',

  /** Admin gift order operations (read-only browse). */
  adminGiftOrders: '/admin/gift-orders',

  /** Admin Goody category to occasion hint mapping. */
  adminGiftCategories: '/admin/gifts/categories',

  /** Admin nurture email template editor (PBI #1095). */
  adminNurtureEmails: '/admin/nurture-emails',

  /** Admin Email stats — archived outbound mail (PBI #1146). */
  adminEmailStats: '/admin/email-stats',

  /** SuperAdmin GlockApps deliverability campaign queue (PBI #1125). */
  adminGlockApps: '/admin/email-deliverability',

  /** SuperAdmin POAS reconciliation for Google Shopping product orders (PBI #1208). */
  adminMarketingPoas: '/admin/marketing/poas',

  /** SuperAdmin host-wide assistant grant revoke (PBI #1321). */
  adminAssistantHosts: '/admin/assistant-hosts',
} as const;

/**
 * Builds the user-facing URL for a single support ticket thread.
 * Centralized so the route shape is owned in one file.
 */
export const buildContactUsThreadPath = (ticketId: number): string =>
  `/contact-us/${ticketId}`;

/**
 * Builds the admin-facing URL for a single support ticket detail page.
 */
export const buildAdminSupportTicketPath = (ticketId: number): string =>
  `/admin/support-tickets/${ticketId}`;

/** Authenticated contacts list. */
export const CONTACTS_PATH = '/contacts';

/**
 * Query keys for Contacts deep links. Reminder emails use contact + event to
 * open the e-card schedule wizard. Flower/mailed success links also send
 * `flowerOrder` / `mailedCardOrder` so we select the contact without opening
 * that wizard.
 */
export const ContactsQueryParams = {
  contactId: 'contactId',
  eventTypeId: 'eventTypeId',
  scheduleContact: 'scheduleContact',
  scheduleEvent: 'scheduleEvent',
  flowerOrder: 'flowerOrder',
  mailedCardOrder: 'mailedCardOrder',
  /** Opens the edit-contact modal for the given contact id (agent MCP contactDetailUrl). */
  editContact: 'editContact',
} as const;

/**
 * Contacts URL that selects the recipient and opens the flower pre-order.
 */
export function buildContactsFlowerOrderPath(order: {
  contactId: number;
  eventTypeId: number;
  id: number;
}): string {
  const params = new URLSearchParams({
    [ContactsQueryParams.contactId]: String(order.contactId),
    [ContactsQueryParams.eventTypeId]: String(order.eventTypeId),
    [ContactsQueryParams.flowerOrder]: String(order.id),
  });
  return `${CONTACTS_PATH}?${params.toString()}`;
}

/**
 * Contacts URL that selects the recipient and opens the edit-contact modal.
 * Matches agent `contactDetailUrl` when optional fields are still missing.
 */
export function buildContactsEditContactPath(contactId: number): string {
  const params = new URLSearchParams({
    [ContactsQueryParams.editContact]: String(contactId),
  });
  return `${CONTACTS_PATH}?${params.toString()}`;
}

